<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert :message="alertMessage" :show="alertShow" :type="alertType"></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- ─── En-tête ─── -->
			<div class="stk-header">
				<div class="stk-header-left">
					<div class="stk-icon-box">
						<v-icon size="18" color="white">mdi-package-variant-closed</v-icon>
					</div>
					<div>
						<p class="stk-title">Stock</p>
						<p class="stk-subtitle">{{ activeTab === 0 ? 'Gestion des produits' : activeTab === 1 ? 'Sorties de stock' : 'Statistiques de ventes' }}</p>
					</div>
				</div>

				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined dense hide-details
					class="stk-search"
					color="blue darken-2"
				></v-text-field>

				<v-btn-toggle
					v-if="activeTab === 2"
					v-model="ventesPeriod"
					mandatory dense class="stk-period-toggle"
				>
					<v-btn value="day" small class="stk-period-btn">Jour</v-btn>
					<v-btn value="week" small class="stk-period-btn">Semaine</v-btn>
					<v-btn value="month" small class="stk-period-btn">Mois</v-btn>
					<v-btn value="year" small class="stk-period-btn">Année</v-btn>
				</v-btn-toggle>
				<v-btn
					v-if="activeTab === 0"
					depressed rounded class="stk-add-btn"
					@click="dialog = true"
				>
					<v-icon left small>mdi-plus</v-icon>
					Nouveau produit
				</v-btn>
				<v-btn
					v-if="activeTab === 1"
					depressed rounded class="stk-exit-btn"
					@click="dialogExit = true"
				>
					<v-icon left small>mdi-arrow-right-bold-box</v-icon>
					Nouvelle sortie
				</v-btn>
			</div>

			<v-divider></v-divider>

			<!-- ─── Onglets ─── -->
			<v-tabs v-model="activeTab" class="stk-tabs" color="#1565c0">
				<v-tab>
					<v-icon left small>mdi-package-variant-closed</v-icon>
					Inventaire
				</v-tab>
				<v-tab>
					<v-icon left small>mdi-arrow-right-bold-box</v-icon>
					Sorties de stock
				</v-tab>
				<v-tab>
					<v-icon left small>mdi-chart-bar</v-icon>
					Ventes
				</v-tab>
			</v-tabs>

			<v-divider></v-divider>

			<v-tabs-items v-model="activeTab">
				<!-- ─── Onglet 0 : Inventaire ─── -->
				<v-tab-item>
					<v-data-table
						:headers="headers"
						:items="products"
						:server-items-length="totalItems"
						:options.sync="options"
						:loading="loading"
						loading-text="Chargement en cours..."
						class="stk-table"
						:footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Lignes par page', 'items-per-page-all-text': 'Tout' }"
					>
						<template v-slot:[`item.fournisseur`]="{ item }">
							<span v-if="item.fournisseur && item.fournisseur.name" class="stk-supplier">
								<v-icon x-small color="#9ca3af" class="mr-1">mdi-truck-delivery-outline</v-icon>
								{{ item.fournisseur.name }}
							</span>
							<span v-else class="stk-no-value">—</span>
						</template>
						<template v-slot:[`item.unitPrice`]="{ item }">
							{{ item.unitPrice.toLocaleString('fr') }} FCFA
						</template>
						<template v-slot:[`item.totalValue`]="{ item }">
							<span class="stk-total">{{ (item.quantity * item.unitPrice).toLocaleString('fr') }} FCFA</span>
						</template>
						<template v-slot:[`item.actions`]="{ item }">
							<v-btn icon small @click="editItem(item)"><v-icon size="17" color="#374151">mdi-pencil</v-icon></v-btn>
							<v-btn icon small @click="deleteItem(item)"><v-icon size="17" color="#c62828">mdi-delete</v-icon></v-btn>
						</template>
						<template v-slot:[`footer.page-text`]="items">{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}</template>
						<template v-slot:no-data>
							<v-btn text color="primary" @click="loadProducts">Actualiser</v-btn>
						</template>
					</v-data-table>
				</v-tab-item>

				<!-- ─── Onglet 1 : Sorties ─── -->
				<v-tab-item>
					<v-data-table
						:headers="exitHeaders"
						:items="exits"
						:server-items-length="totalExits"
						:options.sync="optionsExit"
						:loading="loadingExits"
						loading-text="Chargement en cours..."
						class="stk-table"
						:footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Lignes par page', 'items-per-page-all-text': 'Tout' }"
					>
						<template v-slot:[`item.paid`]="{ item }">
							<v-chip small :color="item.paid ? '#d1fae5' : '#fee2e2'" :text-color="item.paid ? '#065f46' : '#991b1b'">
								{{ item.paid ? 'Payé' : 'Non payé' }}
							</v-chip>
						</template>
						<template v-slot:[`item.createdAt`]="{ item }">
							{{ new Date(item.createdAt).toLocaleDateString('fr-FR') }}
						</template>
						<template v-slot:[`item.actions`]="{ item }">
							<v-btn icon small @click="deleteExit(item)"><v-icon size="17" color="#c62828">mdi-delete</v-icon></v-btn>
						</template>
						<template v-slot:[`footer.page-text`]="items">{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}</template>
						<template v-slot:no-data>
							<v-btn text color="primary" @click="loadExits">Actualiser</v-btn>
						</template>
					</v-data-table>
				</v-tab-item>
				<!-- ─── Onglet 2 : Ventes ─── -->
				<v-tab-item>
					<div v-if="loadingVentes" class="ventes-loading">
						<v-progress-circular indeterminate size="32" color="#00695c"></v-progress-circular>
					</div>
					<div v-else>
						<!-- KPI -->
						<div class="ventes-kpi-row pa-4">
							<div class="ventes-kpi-card" style="border-color:#00695c;">
								<div class="ventes-kpi-icon" style="background:#00695c;">
									<v-icon size="16" color="white">mdi-cash-multiple</v-icon>
								</div>
								<div>
									<p class="ventes-kpi-val">{{ (ventesTotalRevenue || 0).toLocaleString('fr') }} FCFA</p>
									<p class="ventes-kpi-lbl">Chiffre d'affaires cumulé</p>
									<p class="ventes-kpi-hint">Σ qté × prix unitaire</p>
								</div>
							</div>
							<div class="ventes-kpi-card" style="border-color:#1565c0;">
								<div class="ventes-kpi-icon" style="background:#1565c0;">
									<v-icon size="16" color="white">mdi-package-variant-closed</v-icon>
								</div>
								<div>
									<p class="ventes-kpi-val">{{ (ventesTotalQuantity || 0).toLocaleString('fr') }}</p>
									<p class="ventes-kpi-lbl">Unités vendues</p>
									<p class="ventes-kpi-hint">{{ ventesStats.length }} produit{{ ventesStats.length > 1 ? 's' : '' }}</p>
								</div>
							</div>
							<div class="ventes-kpi-card" style="border-color:#7c3aed;">
								<div class="ventes-kpi-icon" style="background:#7c3aed;">
									<v-icon size="16" color="white">mdi-receipt-text</v-icon>
								</div>
								<div>
									<p class="ventes-kpi-val">{{ ventesTotalTransactions }}</p>
									<p class="ventes-kpi-lbl">Transactions</p>
									<p class="ventes-kpi-hint">sorties enregistrées</p>
								</div>
							</div>
						</div>

						<v-divider></v-divider>

						<!-- Pas de ventes -->
						<div v-if="ventesStats.length === 0" class="ventes-empty">
							<v-icon size="48" color="#e5e7eb">mdi-chart-bar</v-icon>
							<p class="ventes-empty-text">Aucune vente sur cette période</p>
						</div>

						<!-- Liste produits -->
						<v-expansion-panels v-else flat accordion class="ventes-panels">
							<v-expansion-panel
								v-for="stat in ventesStats"
								:key="stat.productName"
								class="ventes-panel"
							>
								<v-expansion-panel-header class="ventes-panel-header">
									<div class="ventes-panel-summary">
										<div class="ventes-panel-name">
											<v-icon size="16" color="#1565c0" class="mr-2">mdi-package-variant-closed</v-icon>
											{{ stat.productName }}
										</div>
										<div class="ventes-panel-chips">
											<v-chip x-small color="#dbeafe" text-color="#1e40af" class="mr-2">
												{{ stat.quantitySold }} unités
											</v-chip>
											<v-chip x-small color="#d1fae5" text-color="#065f46">
												{{ (stat.revenue || 0).toLocaleString('fr') }} FCFA
											</v-chip>
										</div>
									</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<div v-if="!stat.sales || stat.sales.length === 0" class="ventes-no-detail">
										Pas de détail disponible.
									</div>
									<v-simple-table v-else dense class="ventes-detail-table">
										<thead>
											<tr>
												<th>Date</th>
												<th>Qté</th>
												<th>Prix unit.</th>
												<th>Valeur totale</th>
												<th>Paiement</th>
												<th>Motif</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="(sale, i) in stat.sales" :key="i">
												<td>{{ new Date(sale.createdAt).toLocaleDateString('fr-FR') }}</td>
												<td>{{ sale.quantity }}</td>
												<td class="ventes-unit-price">{{ (sale.unitPrice || 0).toLocaleString('fr') }} FCFA</td>
												<td class="ventes-amt">{{ (sale.total || 0).toLocaleString('fr') }} FCFA</td>
												<td>
													<v-chip x-small :color="sale.paid ? '#d1fae5' : '#fee2e2'" :text-color="sale.paid ? '#065f46' : '#991b1b'">
														{{ sale.paid ? 'Payé' : 'Non payé' }}
													</v-chip>
												</td>
												<td>{{ sale.reason || '—' }}</td>
											</tr>
										</tbody>
									</v-simple-table>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>
				</v-tab-item>
			</v-tabs-items>
		</v-card>

		<!-- Dialog : Nouveau produit -->
		<v-dialog v-model="dialog" max-width="480px">
			<v-card rounded="xl" class="pa-2">
				<div class="form-dialog-header">
					<div class="form-dialog-icon" style="background:#1565c0;">
						<v-icon size="14" color="white">mdi-package-variant-closed</v-icon>
					</div>
					<div>
						<p class="form-dialog-title">{{ formTitle }}</p>
						<p class="form-dialog-sub">Produit en stock</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeDialog"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="form" v-model="valid" lazy-validation>
						<v-text-field
							v-model="form.name"
							label="Nom du produit"
							outlined dense rounded
							prepend-inner-icon="mdi-tag"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto" class="mb-4"
						></v-text-field>
						<v-text-field
							v-model="form.description"
							label="Description (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-text"
							hide-details class="mb-4"
						></v-text-field>
						<v-row class="mb-4">
							<v-col cols="6">
								<v-text-field
									v-model.number="form.quantity"
									label="Quantité"
									outlined dense rounded
									prepend-inner-icon="mdi-counter"
									type="number" min="0"
									:rules="[v => (v !== null && v !== '') || 'Champ requis', v => v >= 0 || 'Invalide']"
									hide-details="auto"
								></v-text-field>
							</v-col>
							<v-col cols="6">
								<v-text-field
									v-model.number="form.unitPrice"
									label="Prix unitaire"
									outlined dense rounded
									prepend-inner-icon="mdi-cash"
									suffix="FCFA" type="number" min="0"
									:rules="[v => (v !== null && v !== '') || 'Champ requis', v => v >= 0 || 'Invalide']"
									hide-details="auto"
								></v-text-field>
							</v-col>
						</v-row>
						<v-autocomplete
							v-model="form.fournisseur"
							:items="supplierOptions"
							item-text="name"
							item-value="_id"
							label="Fournisseur (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-truck-delivery-outline"
							clearable
							hide-details
							:filter="() => true"
						></v-autocomplete>
					</v-form>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey darken-1" @click="closeDialog">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn :disabled="!valid" depressed rounded class="stk-save-btn" @click="save">Enregistrer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog : Nouvelle sortie -->
		<v-dialog v-model="dialogExit" max-width="480px">
			<v-card rounded="xl" class="pa-2">
				<div class="form-dialog-header">
					<div class="form-dialog-icon" style="background:#c62828;">
						<v-icon size="14" color="white">mdi-arrow-right-bold-box</v-icon>
					</div>
					<div>
						<p class="form-dialog-title">Enregistrer une sortie</p>
						<p class="form-dialog-sub">Sortie de stock</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeExitDialog"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="exitForm" v-model="validExit" lazy-validation>
						<v-autocomplete
							v-model="exitForm.product"
							:items="productOptions"
							item-text="name"
							item-value="_id"
							label="Produit"
							outlined dense rounded
							prepend-inner-icon="mdi-package-variant-closed"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto" class="mb-4"
							:filter="() => true"
						></v-autocomplete>
						<v-text-field
							v-model.number="exitForm.quantity"
							label="Quantité sortie"
							outlined dense rounded
							prepend-inner-icon="mdi-counter"
							type="number" min="1"
							:max="selectedProductStock !== null ? selectedProductStock : undefined"
							:rules="[
								v => (v !== null && v !== '') || 'Champ requis',
								v => v > 0 || 'Invalide',
								v => selectedProductStock === null || v <= selectedProductStock || `Stock insuffisant (max : ${selectedProductStock})`
							]"
							:hint="selectedProductStock !== null ? `Stock disponible : ${selectedProductStock}` : ''"
							:persistent-hint="selectedProductStock !== null"
							class="mb-4"
						></v-text-field>
						<v-text-field
							v-model="exitForm.reason"
							label="Motif (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-text"
							hide-details class="mb-4"
						></v-text-field>
						<div class="mt-1">
							<p class="field-lbl">Paiement effectué ?</p>
							<v-btn-toggle v-model="exitForm.paid" mandatory dense class="toggle-group">
								<v-btn :value="true" small class="toggle-opt">
									<v-icon left x-small>mdi-check-circle</v-icon> Oui
								</v-btn>
								<v-btn :value="false" small class="toggle-opt">
									<v-icon left x-small>mdi-close-circle</v-icon> Non
								</v-btn>
							</v-btn-toggle>
						</div>
					</v-form>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey darken-1" @click="closeExitDialog">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn :disabled="!validExit" depressed rounded class="stk-exit-save-btn" @click="saveExit">Enregistrer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog : Supprimer produit -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer ce produit ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="dialogDelete = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog : Supprimer sortie -->
		<v-dialog v-model="dialogDeleteExit" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer cette sortie ?</p>
				</div>
				<v-card-text class="pb-1">
					<p class="del-warn">La quantité sera réintégrée au stock du produit.</p>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="dialogDeleteExit = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteExitConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import service from "../services";
export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		loaderDialog: false,
		alertMessage: "",
		alertShow: false,
		alertType: "",
		activeTab: 0,
		search: "",
		searchTimeout: null,

		// Inventaire
		dialog: false,
		dialogDelete: false,
		valid: false,
		loading: false,
		options: {},
		totalItems: 0,
		products: [],
		editedId: null,
		form: { name: "", description: "", quantity: 0, unitPrice: 0, fournisseur: null },
		defaultForm: { name: "", description: "", quantity: 0, unitPrice: 0, fournisseur: null },
		headers: [
			{ text: "Produit", value: "name", sortable: false },
			{ text: "Fournisseur", value: "fournisseur", sortable: false },
			{ text: "Quantité", value: "quantity", sortable: false },
			{ text: "Prix unitaire", value: "unitPrice", sortable: false },
			{ text: "Valeur totale", value: "totalValue", sortable: false },
			{ text: "Actions", value: "actions", sortable: false, width: "100px" },
		],
		supplierOptions: [],

		// Sorties
		dialogExit: false,
		dialogDeleteExit: false,
		validExit: false,
		loadingExits: false,
		optionsExit: {},
		totalExits: 0,
		exits: [],
		exitedId: null,
		exitForm: { product: null, quantity: 0, reason: "", paid: false },
		defaultExitForm: { product: null, quantity: 0, reason: "", paid: false },
		exitHeaders: [
			{ text: "Produit", value: "productName", sortable: false },
			{ text: "Quantité sortie", value: "quantity", sortable: false },
			{ text: "Motif", value: "reason", sortable: false },
			{ text: "Paiement", value: "paid", sortable: false },
			{ text: "Date", value: "createdAt", sortable: false },
			{ text: "Actions", value: "actions", sortable: false, width: "80px" },
		],
		productOptions: [],

		// Ventes
		ventesPeriod: 'month',
		loadingVentes: false,
		ventesStats: [],
		ventesTotalRevenue: 0,
		ventesTotalQuantity: 0,
	}),
	computed: {
		formTitle() { return this.editedId ? "Modifier le produit" : "Nouveau produit"; },
		ventesTotalTransactions() {
			return this.ventesStats.reduce((sum, s) => sum + (s.sales ? s.sales.length : 0), 0);
		},
		selectedProductStock() {
			if (!this.exitForm.product) return null;
			const p = this.productOptions.find(p => p._id === this.exitForm.product);
			return p ? p.quantity : null;
		},
	},
	watch: {
		options: { handler() { this.loadProducts(); }, deep: true },
		optionsExit: { handler() { this.loadExits(); }, deep: true },
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.activeTab === 0) {
					if (this.options.page !== 1) this.options.page = 1;
					else this.loadProducts();
				} else {
					if (this.optionsExit.page !== 1) this.optionsExit.page = 1;
					else this.loadExits();
				}
			}, 400);
		},
		activeTab(val) {
			this.search = "";
			if (val === 0) this.loadProducts();
			else if (val === 1) this.loadExits();
			else this.loadVentes();
		},
		ventesPeriod() { this.loadVentes(); },
		dialog(val) {
			if (val) this.loadSuppliers();
			else this.closeDialog();
		},
		dialogExit(val) {
			if (val) this.loadProductOptions();
			else this.closeExitDialog();
		},
	},
	created() {
		this.loadSuppliers();
	},
	methods: {
		loadProducts() {
			this.loading = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getProducts({ page, limit, search: this.search })
				.then((r) => {
					this.products = r.data?.products || [];
					this.totalItems = (r.data?.totalPages ?? 0) * limit;
					this.loading = false;
				})
				.catch((e) => { console.log(e); this.loading = false; });
		},
		loadExits() {
			this.loadingExits = true;
			const page = this.optionsExit.page || 1;
			const limit = this.optionsExit.itemsPerPage || 10;
			service.getStockExits({ page, limit, search: this.search })
				.then((r) => {
					this.exits = r.data?.sorties || [];
					this.totalExits = (r.data?.totalPages ?? 0) * limit;
					this.loadingExits = false;
				})
				.catch((e) => { console.log(e); this.loadingExits = false; });
		},
		loadSuppliers() {
			service.getSuppliers({ limit: 200 })
				.then((r) => { this.supplierOptions = r.data?.fournisseurs || []; })
				.catch(() => {});
		},
		loadProductOptions() {
			service.getProducts({ limit: 200 })
				.then((r) => { this.productOptions = r.data?.products || []; })
				.catch(() => {});
		},
		manageAlert(show, type, msg) { this.alertShow = show; this.alertType = type; this.alertMessage = msg; },

		// ─── Produits ───
		editItem(item) {
			this.editedId = item._id;
			this.form = {
				name: item.name,
				description: item.description || "",
				quantity: item.quantity,
				unitPrice: item.unitPrice,
				fournisseur: item.fournisseur?._id || item.fournisseur || null,
			};
			this.dialog = true;
		},
		deleteItem(item) { this.editedId = item._id; this.dialogDelete = true; },
		deleteConfirm() {
			this.loaderDialog = true;
			service.deleteProduct(this.editedId)
				.then(() => {
					this.loaderDialog = false;
					this.dialogDelete = false;
					this.editedId = null;
					this.loadProducts();
				})
				.catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
		closeDialog() {
			this.dialog = false;
			this.$nextTick(() => {
				this.form = Object.assign({}, this.defaultForm);
				this.editedId = null;
				if (this.$refs.form) this.$refs.form.resetValidation();
			});
		},
		save() {
			if (!this.$refs.form.validate()) return;
			this.loaderDialog = true;
			const p = this.editedId
				? service.updateProduct(this.editedId, this.form)
				: service.createProduct(this.form);
			p.then(() => {
				this.loaderDialog = false;
				this.manageAlert(true, "success", "Produit enregistré !");
				this.closeDialog();
				this.loadProducts();
			}).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},

		// ─── Sorties ───
		deleteExit(item) { this.exitedId = item._id; this.dialogDeleteExit = true; },
		deleteExitConfirm() {
			this.loaderDialog = true;
			service.deleteStockExit(this.exitedId)
				.then(() => {
					this.loaderDialog = false;
					this.dialogDeleteExit = false;
					this.exitedId = null;
					this.loadExits();
					this.loadProducts();
				})
				.catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
		closeExitDialog() {
			this.dialogExit = false;
			this.$nextTick(() => {
				this.exitForm = Object.assign({}, this.defaultExitForm);
				if (this.$refs.exitForm) this.$refs.exitForm.resetValidation();
			});
		},
		// ─── Ventes ───
		loadVentes() {
			this.loadingVentes = true;
			service.getVentesStats({ period: this.ventesPeriod })
				.then((r) => {
					this.ventesStats = r.data?.stats || [];
					this.ventesTotalRevenue = r.data?.totalRevenue || 0;
					this.ventesTotalQuantity = r.data?.totalQuantity || 0;
					this.loadingVentes = false;
				})
				.catch((e) => { console.log(e); this.loadingVentes = false; });
		},

		saveExit() {
			if (!this.$refs.exitForm.validate()) return;
			this.loaderDialog = true;
			service.createStockExit(this.exitForm)
				.then(() => {
					this.loaderDialog = false;
					this.manageAlert(true, "success", "Sortie enregistrée !");
					this.closeExitDialog();
					this.loadExits();
					this.loadProducts();
				})
				.catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
	},
};
</script>

<style scoped>
/* ─── En-tête ─── */
.stk-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.stk-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.stk-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #1565c0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.stk-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.stk-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.stk-search { max-width: 240px; }
.stk-add-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(21,101,192,0.28) !important; letter-spacing: 0.3px; }
.stk-exit-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; letter-spacing: 0.3px; }

/* ─── Onglets ─── */
.stk-tabs { font-family: Montserrat, sans-serif; }
.stk-tabs .v-tab { font-family: Montserrat, sans-serif; font-weight: 600; font-size: 0.82rem; text-transform: none; letter-spacing: 0.2px; }

/* ─── Table ─── */
.stk-table { font-family: Montserrat, sans-serif; }
.stk-total { font-weight: 700; color: #00695c; }
.stk-supplier { display: inline-flex; align-items: center; font-size: 0.78rem; color: #374151; }
.stk-no-value { color: #d1d5db; font-size: 0.8rem; }

/* ─── Dialog formulaire ─── */
.form-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.form-dialog-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.form-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.form-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.stk-save-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(21,101,192,0.28) !important; letter-spacing: 0.3px; }
.stk-exit-save-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; letter-spacing: 0.3px; }

/* ─── Paiement toggle ─── */
.field-lbl { font-size: 0.75rem; font-weight: 600; color: #6b7280; font-family: Montserrat, sans-serif; margin: 0 0 8px; }
.toggle-group .toggle-opt { font-family: Montserrat, sans-serif; font-size: 0.78rem; font-weight: 600; text-transform: none; letter-spacing: 0; }

/* ─── Dialog suppression ─── */
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.del-warn { font-size: 0.78rem; color: #6b7280; font-family: Montserrat, sans-serif; background: #fef2f2; border: 1px solid #fee2e2; border-radius: 8px; padding: 10px 12px; margin: 0; }
.del-confirm-btn { background: #b91c1c !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 3px 10px rgba(185,28,28,0.3) !important; }

/* ─── Période toggle ─── */
.stk-period-toggle { border-radius: 8px !important; }
.stk-period-btn { font-family: Montserrat, sans-serif !important; font-size: 0.75rem !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: 0 !important; }

/* ─── Ventes ─── */
.ventes-loading { display: flex; justify-content: center; padding: 60px; }
.ventes-kpi-row { display: flex; gap: 16px; flex-wrap: wrap; }
.ventes-kpi-card { display: flex; align-items: center; gap: 12px; background: white; border: 1.5px solid; border-radius: 12px; padding: 12px 16px; flex: 1; min-width: 180px; font-family: Montserrat, sans-serif; }
.ventes-kpi-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ventes-kpi-val { font-size: 1.1rem; font-weight: 800; color: #111827; margin: 0 0 2px; }
.ventes-kpi-lbl { font-size: 0.65rem; font-weight: 600; color: #9ca3af; margin: 0; text-transform: uppercase; letter-spacing: 0.5px; }
.ventes-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 60px 20px; font-family: Montserrat, sans-serif; }
.ventes-empty-text { font-size: 0.9rem; font-weight: 700; color: #6b7280; margin: 0; }
.ventes-panels { font-family: Montserrat, sans-serif; }
.ventes-panel { border-bottom: 1px solid #f3f4f6 !important; }
.ventes-panel-header { font-family: Montserrat, sans-serif !important; padding: 0 16px !important; min-height: 52px !important; }
.ventes-panel-summary { display: flex; align-items: center; justify-content: space-between; flex: 1; padding-right: 12px; }
.ventes-panel-name { display: flex; align-items: center; font-size: 0.85rem; font-weight: 700; color: #111827; }
.ventes-panel-chips { display: flex; align-items: center; gap: 4px; }
.ventes-no-detail { font-size: 0.78rem; color: #9ca3af; padding: 12px 0; font-family: Montserrat, sans-serif; }
.ventes-detail-table { font-family: Montserrat, sans-serif; font-size: 0.8rem; }
.ventes-detail-table th { font-size: 0.72rem !important; font-weight: 700 !important; color: #6b7280 !important; text-transform: uppercase; letter-spacing: 0.5px; }
.ventes-amt { font-weight: 700; color: #00695c; }
.ventes-unit-price { color: #6b7280; font-size: 0.78rem; }
.ventes-kpi-hint { font-size: 0.6rem; color: #d1d5db; margin: 0; font-family: Montserrat, sans-serif; }
</style>
